import React, { useState } from "react";
import { Loader2, MessageCircle, Send, X } from "lucide-react";
import CommentItem from "./CommentItem";
import CommentActionButtons from "./CommentActionButtons";
import { useAuth } from "../../../context/AuthContext";
import { useComments } from "./useComments";
import { registerStyles } from "../../../utils/commentStyles";
import ReportModal from "../../Modal/Reportmodal";
import ErrorModal from "../../Modal/ErrorModal";

// Register styles on component load
registerStyles();

const CommentsModal = ({
  postId,
  postAuthorProfileId,
  onClose,
  queryKey = ["feedPosts", "for_you"],
}) => {
  const { apiCall, user } = useAuth();

  const {
    comments,
    loading,
    error,
    actionError,
    setActionError,
    hasMore,
    loadingMore,
    newComment,
    setNewComment,
    posting,
    replyingTo,
    setReplyingTo,
    activeCommentId,
    setActiveCommentId,
    inputRef,
    handleScroll,
    handlePostComment,
    handleReply,
    handleLikeToggle,
    handleShowActionMenu,
    handleDeleteComment,
  } = useComments(postId, apiCall, queryKey);

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportSuccessMsg, setReportSuccessMsg] = useState(null);

  const handlePostCommentWithUser = async () => {
    await handlePostComment(user);
  };

  const handleReportComment = () => {
    setIsReportModalOpen(true);
  };

  const handleCloseReport = () => {
    setIsReportModalOpen(false);
    setActiveCommentId(null);
  };

  const handleReportSuccess = (message) => {
    setIsReportModalOpen(false);
    setActiveCommentId(null);
    setReportSuccessMsg(message);
    setTimeout(() => setReportSuccessMsg(null), 3000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="relative w-full bg-white rounded-t-3xl max-h-[85vh] flex flex-col animate-slide-up px-2 sm:px-0 sm:max-w-xl sm:mx-auto">
        {/* Header */}
        <div className="flex items-center justify-center p-2 sm:p-3 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-base sm:text-lg font-semibold text-black">
            Comments
          </h3>
        </div>

        {/* Comments List */}
        <div
          className="h-[40vh] overflow-y-auto pt-3 sm:p-4 space-y-4 relative"
          onScroll={handleScroll}
          onClick={(e) => {
            if (!e.target.closest(".comment-item-wrapper") && activeCommentId) {
              setActiveCommentId(null);
            }
          }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin mb-2" />
              <p className="text-sm">Loading comments...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <MessageCircle className="w-8 h-8 mb-2" />
              <p className="text-sm">{error}</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <MessageCircle className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">No comments yet</p>
              <p className="text-xs opacity-70">Be the first to comment</p>
            </div>
          ) : (
            <>
              {comments.map((comment) => (
                <div
                  key={comment._id}
                  className={`comment-item-wrapper relative transition-all duration-300 ${activeCommentId && activeCommentId !== comment._id
                      ? "pointer-events-none"
                      : ""
                    }`}
                  onClick={(e) => {
                    if (
                      activeCommentId === comment._id &&
                      !e.target.closest("button")
                    ) {
                      setActiveCommentId(null);
                    }
                  }}
                >
                  <CommentItem
                    comment={comment}
                    apiCall={apiCall}
                    onReply={handleReply}
                    currentUserProfileId={user?.profile?.id}
                    onLikeToggle={handleLikeToggle}
                    onShowActionMenu={handleShowActionMenu}
                    activeCommentId={activeCommentId}
                    onDeleteComment={handleDeleteComment}
                    onReport={handleReportComment}
                  />

                  {activeCommentId === comment._id && (
                    <CommentActionButtons
                      comment={comment}
                      currentUserProfileId={user?.profile?.id}
                      onDelete={(commentId) => {
                        handleDeleteComment(commentId);
                        setActiveCommentId(null);
                      }}
                      onReport={handleReportComment}
                    />
                  )}
                </div>
              ))}

              {loadingMore && (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}

              {!hasMore && comments.length > 0 && (
                <div className="text-center py-3 text-xs text-gray-400">
                  No more comments
                </div>
              )}
            </>
          )}
        </div>

        {/* Post Comment Input */}
        <div className="border-t border-gray-200 py-4 sm:px-5 px-2 bg-white flex-shrink-0">
          {replyingTo && (
            <div className="mb-2 flex items-center gap-2 text-sm text-gray-500">
              <span>Replying to {replyingTo.author.username}</span>
              <button
                onClick={() => {
                  setReplyingTo(null);
                  setNewComment("");
                }}
                className="text-red-500 hover:text-red-600 transition-colors text-sm font-semibold"
              >
                Cancel
              </button>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <input
                ref={inputRef}
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && handlePostCommentWithUser()
                }
                placeholder={
                  replyingTo
                    ? `Reply to @${replyingTo.author.username}`
                    : "Add a comment"
                }
                disabled={posting}
                className="w-full bg-gray-100 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 placeholder-gray-500 disabled:opacity-50 transition-all"
              />
            </div>

            <button
              onClick={handlePostCommentWithUser}
              disabled={!newComment.trim() || posting}
              className="p-2 rounded-full text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#4f45e7" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#4338ca")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#4f45e7")
              }
            >
              {posting ? (
                <Loader2
                  size={18}
                  strokeWidth={2.5}
                  className="animate-spin"
                />
              ) : (
                <Send size={18} strokeWidth={2.5} />
              )}
            </button>
          </div>
        </div>
      </div>
      {isReportModalOpen && activeCommentId && (
        <ReportModal
          targetId={activeCommentId}
          targetType="comment"
          onClose={handleCloseReport}
          onSuccess={handleReportSuccess}
        />
      )}

      {reportSuccessMsg && (
        <div className="fixed bottom-6 inset-x-4 z-[70] flex justify-center">
          <div className="w-full max-w-md bg-black text-white text-sm font-medium px-4 py-3 rounded-full shadow-lg text-center">
            {reportSuccessMsg}
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={!!actionError}
        message={actionError}
        onClose={() => setActionError(null)}
      />
    </div>
  );
};

export default CommentsModal;